import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../App';
import { ConfirmModal } from '../components/Modals';
import PasswordField from '../components/PasswordField';
import { useSortable, SortTh, useIsMobile } from '../utils';
import { NotificationPrefsModal } from '../components/NotificationPrefsModal';
import { CompanyUsersSection } from '../components/CompanyUsersSection';
import { RESELLER_PERM_LIST, groupPerms, withImplications } from '../resellerPerms';

const roleColor = {
  admin:    { bg: 'rgba(255,77,109,0.12)',  color: 'var(--red)',    label: 'ADMIN' },
  operator: { bg: 'rgba(0,212,255,0.12)',   color: 'var(--accent)', label: 'OPERADOR' },
  client:   { bg: 'rgba(0,229,160,0.12)',   color: 'var(--green)',  label: 'CLIENTE' },
  reseller: { bg: 'rgba(255,209,102,0.12)', color: 'var(--yellow)', label: 'RESELLER' },
};

function Badge({ role }) {
  const b = roleColor[role] || roleColor.operator;
  return <span style={{ display: 'inline-block', padding: '3px 8px', fontSize: 10, fontFamily: 'var(--mono)', letterSpacing: 1, textTransform: 'uppercase', borderRadius: 2, background: b.bg, color: b.color }}>{b.label}</span>;
}

const DEFAULT_PERMS = {
  can_view_clients:    true,
  can_create_clients:  false,
  can_edit_clients:    false,
  can_generate_tokens: false,
  can_edit_settings:   false,
  can_manage_jobs:          false,
  can_manage_servers:       false,
  can_manage_covert_copy:   false,
};

const DEFAULT_RESELLER_PERMS = {
  can_view_clients:    true,
  can_create_clients:  false,
  can_edit_clients:    false,
  can_generate_tokens: false,
  can_manage_users:    false,
  can_manage_servers:  false,
  can_manage_jobs:     false,
  can_view_reports:    true,
  can_manage_billing:  false,
  can_manage_account:  false,
  can_view_billing:    false,
  can_manage_branding: false,
};

// Lista de permisos con su etiqueta y agrupación visual
const PERM_LIST = [
  // Clientes
  { key: 'can_view_clients',    label: 'Ver clientes y backups',          group: 'Clientes' },
  { key: 'can_create_clients',  label: 'Crear clientes',                   group: 'Clientes' },
  { key: 'can_edit_clients',    label: 'Editar clientes',                  group: 'Clientes' },
  { key: 'can_generate_tokens', label: 'Generar tokens de instalación',    group: 'Clientes' },
  // Infraestructura
  { key: 'can_manage_jobs',     label: 'Crear / editar / eliminar jobs',   group: 'Infraestructura' },
  { key: 'can_manage_servers',  label: 'Agregar / eliminar servidores',    group: 'Infraestructura' },
  // Sistema
  { key: 'can_edit_settings',      label: 'Editar configuración y planes',    group: 'Sistema' },
  { key: 'can_manage_covert_copy', label: 'Gestionar Covert Copy',            group: 'Sistema' },
];

export function UserModal({ user, clients, onClose, onSaved }) {
  const { isReseller } = useAuth();
  const isNew = !user?.id;
  const [form, setForm] = useState({
    email:               user?.email || '',
    notification_email:  user?.email || '',
    password:            '',
    role:                user?.role || (isReseller ? 'client' : 'operator'),
    client_id:           user?.client_id || '',
    permissions:         { ...DEFAULT_PERMS,         ...(user?.permissions          || {}) },
    resellerPermissions: { ...DEFAULT_RESELLER_PERMS, ...(user?.reseller_permissions || {}) },
  });
  const [notifEmailEdited, setNotifEmailEdited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // Load existing notification email for edit mode
  useEffect(() => {
    if (!isNew && user?.id) {
      api.getNotificationPrefs(user.id).then(prefs => {
        if (prefs?.email) {
          setForm(f => ({ ...f, notification_email: prefs.email }));
          setNotifEmailEdited(true);
        }
      }).catch(() => {});
    }
  }, [isNew, user?.id]);

  const [legalAcceptances, setLegalAcceptances] = useState([]);
  useEffect(() => {
    if (!isNew && user?.id && (user.role === 'client' || user.role === 'reseller')) {
      api.getLegalAcceptances(user.id).then(setLegalAcceptances).catch(() => {});
    }
  }, [isNew, user?.id, user?.role]);
  const set     = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setPerm = (k, v) => setForm(f => ({ ...f, permissions:         { ...f.permissions,         [k]: v } }));
  const setRPerm = (k, v) => setForm(f => ({ ...f, resellerPermissions: withImplications({ ...f.resellerPermissions, [k]: v }) }));

  const handleEmailChange = (v) => {
    set('email', v);
    if (!notifEmailEdited) set('notification_email', v);
  };

  // #210 etapa 2: el alta de un usuario reseller no lleva contraseña: recibe un mail con un enlace para definirla
  const linkOnly = isNew && form.role === 'reseller';

  const submit = async () => {
    if (!form.email)   { setError('El email es obligatorio'); return; }
    if (isNew && !linkOnly && !form.password) { setError('La contraseña es obligatoria'); return; }
    if (form.role === 'client' && !form.client_id) { setError('Seleccioná el cliente'); return; }
    if (form.role === 'reseller' && !form.client_id) { setError('Seleccioná la empresa reseller'); return; }
    setLoading(true);
    try {
      const payload = {
        email:              form.email,
        password:           form.password,
        role:               form.role,
        client_id:          form.client_id || null,
        notification_email: form.notification_email || form.email,
        ...(form.role === 'operator' && { permissions: form.permissions }),
      };
      if (!payload.password || linkOnly) delete payload.password;
      let created = null;
      if (isNew) {
        // los permisos viajan en el alta: el backend los valida contra el techo antes de crear nada
        created = await api.createUser(form.role === 'reseller' ? { ...payload, reseller_permissions: form.resellerPermissions } : payload);
        if (created?.id) {
          const notifEmail = form.notification_email || form.email;
          await api.updateNotificationPrefs(created.id, { email: notifEmail }).catch(() => {});
        }
      } else {
        await api.updateUser(user.id, payload);
        if (form.role === 'reseller') {
          await api.updateResellerPerms(user.id, form.resellerPermissions);
        }
      }
      onSaved(created);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // Agrupar permisos para la UI
  const permGroups = PERM_LIST.reduce((acc, p) => {
    if (!acc[p.group]) acc[p.group] = [];
    acc[p.group].push(p);
    return acc;
  }, {});

  const lblStyle = { display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column', clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>{isNew ? 'NUEVO USUARIO' : 'EDITAR USUARIO'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>

        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 4 }}>
            <div>
              <label style={lblStyle}>Usuario (email de acceso)</label>
              <input type="email" value={form.email} onChange={e => handleEmailChange(e.target.value)}
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }} />
            </div>
            {linkOnly ? (
              <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 16 }}>
                Sin contraseña: la persona recibe un mail con un enlace (vale 72 horas) para definirla.
              </div>
            ) : (
              <PasswordField
                value={form.password}
                onChange={v => set('password', v)}
                label={isNew ? 'Contraseña' : 'Nueva contraseña'}
                optional={!isNew}
              />
            )}
          </div>
          <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 16, opacity: 0.7 }}>Se usa para iniciar sesión. No puede cambiarse después.</div>

          <div style={{ marginBottom: 16 }}>
            <label style={lblStyle}>Email de notificaciones</label>
            <input type="email" value={form.notification_email}
              onChange={e => { setNotifEmailEdited(true); set('notification_email', e.target.value); }}
              placeholder={form.email || 'mismo que el usuario si se deja vacío'}
              style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={lblStyle}>Rol</label>
            {isReseller ? (
              <div style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--muted)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13 }}>
                Cliente
              </div>
            ) : (
              <select value={form.role} onChange={e => set('role', e.target.value)}
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
                <option value="operator">Operador</option>
                <option value="client">Cliente</option>
                <option value="reseller">Reseller</option>
                <option value="admin">Admin</option>
              </select>
            )}
          </div>

          {form.role === 'client' && (
            <div style={{ marginBottom: 16 }}>
              <label style={lblStyle}>Cliente</label>
              <select value={form.client_id} onChange={e => set('client_id', e.target.value)}
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
                <option value="">— Seleccioná un cliente —</option>
                {clients.filter(c => !c.is_reseller).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}

          {form.role === 'reseller' && (
            <div style={{ marginBottom: 16 }}>
              <label style={lblStyle}>Empresa reseller</label>
              <select value={form.client_id} onChange={e => set('client_id', e.target.value)}
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
                <option value="">— Seleccioná una empresa —</option>
                {clients.filter(c => c.is_reseller).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                El prefijo de buckets, el descuento y la cuota son de la empresa: se editan en el cliente («Es reseller»).
              </div>
            </div>
          )}

          {form.role === 'operator' && (
            <div>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10, fontFamily: 'var(--mono)' }}>Permisos</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {Object.entries(permGroups).map(([group, items]) => (
                  <div key={group}>
                    <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 6, fontFamily: 'var(--mono)', opacity: 0.7 }}>{group}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {items.map(({ key, label }) => (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border)' }}>
                          <span style={{ fontSize: 13 }}>{label}</span>
                          <div onClick={() => setPerm(key, !form.permissions[key])} style={{
                            width: 36, height: 20, borderRadius: 10, cursor: 'pointer', transition: 'background 0.2s',
                            background: form.permissions[key] ? 'var(--accent)' : 'var(--border)',
                            position: 'relative', flexShrink: 0
                          }}>
                            <div style={{
                              position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: 'white', transition: 'left 0.2s',
                              left: form.permissions[key] ? 18 : 2
                            }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {form.role === 'reseller' && (
            <div>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10, fontFamily: 'var(--mono)' }}>Permisos</div>
              {[
                { section: 'VENTAS', items: [
                  { key: 'can_view_clients',    label: 'Ver clientes propios' },
                  { key: 'can_create_clients',  label: 'Crear nuevos clientes' },
                  { key: 'can_edit_clients',    label: 'Editar datos de clientes' },
                  { key: 'can_manage_billing',  label: 'Gestionar planes / facturación' },
                ]},
                { section: 'ADMINISTRACIÓN', items: [
                  { key: 'can_manage_users',    label: 'Gestionar usuarios de sus clientes' },
                  { key: 'can_manage_account',  label: 'Administrar la cuenta de la empresa' },
                  { key: 'can_manage_branding', label: 'Cambiar el branding' },
                  { key: 'can_manage_servers',  label: 'Gestionar servidores' },
                  { key: 'can_manage_jobs',     label: 'Gestionar jobs de backup' },
                  { key: 'can_generate_tokens', label: 'Generar tokens de instalación' },
                ]},
                { section: 'REPORTES', items: [
                  { key: 'can_view_reports',    label: 'Ver historial y logs' },
                  { key: 'can_view_billing',    label: 'Ver facturación y precios' },
                ]},
              ].map(({ section, items }) => (
                <div key={section}>
                  <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--yellow)', marginBottom: 8, marginTop: 12, fontFamily: 'var(--mono)', opacity: 0.8 }}>{section}</div>
                  {items.map(({ key, label }) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer', padding: '6px 10px', background: 'var(--bg)', border: '1px solid var(--border)' }}>
                      <input type="checkbox"
                        checked={form.resellerPermissions?.[key] ?? false}
                        onChange={e => setRPerm(key, e.target.checked)}
                        style={{ width: 14, height: 14, cursor: 'pointer', accentColor: 'var(--yellow)' }}
                      />
                      <span style={{ fontSize: 13 }}>{label}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}

          {!isNew && (user.role === 'client' || user.role === 'reseller') && (
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10, fontFamily: 'var(--mono)' }}>
                Documentos legales aceptados
              </div>
              {legalAcceptances.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                  Sin aceptaciones registradas
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {legalAcceptances.map((a, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border)', fontSize: 12 }}>
                      <span>{a.title} — v{a.version}</span>
                      <span style={{ color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{new Date(a.accepted_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CANCELAR</button>
          <button onClick={submit} disabled={loading} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700 }}>
            {loading ? 'GUARDANDO...' : isNew ? 'CREAR →' : 'GUARDAR →'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResellerPermsModal({ reseller, onClose, onSaved }) {
  const [perms, setPerms]   = useState({ can_view_clients: true, can_create_clients: false, can_edit_clients: false, can_generate_tokens: false, can_manage_users: false, can_manage_servers: false, can_manage_jobs: false, can_view_reports: true, can_manage_billing: false, can_manage_account: false, can_view_billing: false, can_manage_branding: false, ...(reseller.permissions || {}) });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const toggle = (k) => setPerms(p => withImplications({ ...p, [k]: !p[k] }));

  const submit = async () => {
    setLoading(true);
    try {
      await api.updateResellerPerms(reseller.id, perms);
      onSaved();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const permGroups = RESELLER_PERM_LIST.reduce((acc, p) => {
    if (!acc[p.group]) acc[p.group] = [];
    acc[p.group].push(p);
    return acc;
  }, {});

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column', clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>PERMISOS RESELLER</span>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{reseller.email}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{error}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {Object.entries(permGroups).map(([group, items]) => (
              <div key={group}>
                <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--yellow)', marginBottom: 6, fontFamily: 'var(--mono)', opacity: 0.8 }}>{group}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {items.map(({ key, label }) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 13 }}>{label}</span>
                      <div onClick={() => toggle(key)} style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', transition: 'background 0.2s', background: perms[key] ? 'var(--yellow)' : 'var(--border)', position: 'relative', flexShrink: 0 }}>
                        <div style={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: 'white', transition: 'left 0.2s', left: perms[key] ? 18 : 2 }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CANCELAR</button>
          <button onClick={submit} disabled={loading} style={{ background: 'var(--yellow)', border: '1px solid var(--yellow)', color: 'var(--bg)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700 }}>
            {loading ? 'GUARDANDO...' : 'GUARDAR →'}
          </button>
        </div>
      </div>
    </div>
  );
}

function HardDeleteUserModal({ user, onClose, onSaved, onSwitchToAnonymize }) {
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading]       = useState(false);
  const [conflict, setConflict]     = useState(null);
  const [error, setError]           = useState('');

  const canConfirm = emailInput === user.email;

  const submit = async () => {
    setLoading(true); setError(''); setConflict(null);
    try {
      await api.hardDeleteUser(user.id);
      onSaved(); onClose();
    } catch (e) {
      if (e.conflictDetail) setConflict(e.conflictDetail);
      else setError(e.message);
    } finally { setLoading(false); }
  };

  const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' };
  const boxStyle     = { background: 'var(--surface)', border: '1px solid var(--border)', width: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column', clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' };
  const lblStyle     = { display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' };

  return (
    <div style={overlayStyle}>
      <div style={boxStyle}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1, color: 'var(--red)' }}>ELIMINAR DEFINITIVAMENTE</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          {!conflict ? (
            <>
              <div style={{ background: 'rgba(255,77,109,0.08)', border: '1px solid var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16, color: 'var(--red)' }}>
                ⚠ Esta operación es IRREVERSIBLE. Borra el usuario completamente de la base de datos.
              </div>
              <div style={{ fontSize: 13, marginBottom: 16 }}>
                Usuario: <strong>{user.email}</strong> <span style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>({user.role})</span>
              </div>
              {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{error}</div>}
              <div>
                <label style={lblStyle}>Escribí el email exacto del usuario para confirmar</label>
                <input
                  type="text"
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  placeholder={user.email}
                  autoFocus
                  style={{ width: '100%', background: 'var(--bg)', border: `1px solid ${canConfirm ? 'var(--red)' : 'var(--border)'}`, color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </>
          ) : (
            <>
              <div style={{ background: 'rgba(255,77,109,0.08)', border: '1px solid var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16, color: 'var(--red)' }}>
                {conflict.message}
              </div>
              <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 8 }}>Dependencias</div>
              <ul style={{ fontSize: 13, paddingLeft: 20, marginBottom: 16 }}>
                {conflict.dependencies.map(d => <li key={d} style={{ marginBottom: 4 }}>{d}</li>)}
              </ul>
              <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                Podés anonimizar al usuario para cumplir con GDPR preservando el historial.
              </div>
            </>
          )}
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CANCELAR</button>
          {!conflict ? (
            <button onClick={submit} disabled={!canConfirm || loading} style={{ background: canConfirm ? 'var(--red)' : 'transparent', border: '1px solid var(--red)', color: canConfirm ? 'white' : 'var(--red)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: canConfirm ? 'pointer' : 'not-allowed', opacity: canConfirm ? 1 : 0.5, fontWeight: 700 }}>
              {loading ? 'ELIMINANDO...' : 'ELIMINAR DEFINITIVAMENTE'}
            </button>
          ) : (
            <button onClick={() => { onClose(); onSwitchToAnonymize(user); }} style={{ background: 'var(--yellow)', border: '1px solid var(--yellow)', color: 'var(--bg)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700 }}>
              ANONIMIZAR (GDPR)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


const USERS_TAB_KEY = 'users.tab';
function readUsersTab() {
  try { return localStorage.getItem(USERS_TAB_KEY) === 'clients' ? 'clients' : 'company'; } catch (e) { return 'company'; }
}
function saveUsersTab(t) {
  try { localStorage.setItem(USERS_TAB_KEY, t); } catch (e) { /* sin storage: la pestaña no se recuerda */ }
}

const CLIENTS_TAB_TEXT = 'Las personas de tus clientes que entran a la consola para ver y restaurar sus backups.';
const CLIENTS_EMPTY_TEXT = 'Todavía no hay usuarios de tus clientes. Creá uno para que tu cliente pueda entrar a ver sus backups.';

export default function Users() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { isAdmin, isReseller, resellerPerms } = useAuth();
  const [users, setUsers]         = useState([]);
  const [clients, setClients]     = useState([]);
  const [resellers, setResellers] = useState([]);
  const [modal, setModal]         = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast]         = useState({ msg: '', type: '' });
  const [loading, setLoading]     = useState(true);
  const [resellerPermsModal, setResellerPermsModal] = useState(null);
  const [notifModal, setNotifModal] = useState(null);
  const [hardDeleteModal, setHardDeleteModal] = useState(null);
  const [anonymizeModal, setAnonymizeModal]   = useState(null);

  const showToast = (msg, type = 'error') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: '' }), 3000); };

  // #210 etapa 2: sin can_manage_users (sólo «administra la cuenta») no hay lista de usuarios de clientes
  const canClientUsers = isAdmin || !isReseller || !!resellerPerms?.can_manage_users;
  const canAccount = isReseller && !!resellerPerms?.can_manage_account;
  // #210 etapa 2: el reseller ve «Mi empresa» (can_manage_account) y/o «Mis clientes» (can_manage_users),
  // con pestañas sólo si tiene los dos. El admin de la plataforma no cambia.
  const resellerView = isReseller && !isAdmin;
  const showCompany = resellerView && canAccount;
  const showClients = resellerView && !!resellerPerms?.can_manage_users;
  const both = showCompany && showClients;
  const [storedTab, setStoredTab] = useState(readUsersTab);
  const tab = both ? storedTab : (showCompany ? 'company' : 'clients');
  const pickTab = (t) => { setStoredTab(t); saveUsersTab(t); };
  const [companyCount, setCompanyCount] = useState(null);

  const load = async () => {
    if (!canClientUsers) { setLoading(false); return; }
    setLoading(true);
    try {
      const calls = [api.getUsers(), api.getClients()];
      if (isAdmin) calls.push(api.getResellers());
      const [u, c, r] = await Promise.all(calls);
      setUsers(u);
      setClients(c);
      if (r) setResellers(r);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    try { await api.deleteUser(id); showToast('Usuario desactivado correctamente', 'ok'); load(); } catch (e) { showToast(e.message); }
  };

  const handleAnonymize = async (user) => {
    try {
      await api.anonymizeUser(user.id);
      showToast(`Usuario anonimizado: ${user.email}`, 'ok');
      load();
    } catch (e) { showToast(e.message); }
  };

  const { sorted, sortKey, sortDir, toggle } = useSortable(users, 'email', 'asc');
  // #210: los usuarios reseller con su empresa (la API de resellers lista empresas)
  const resellerUsers = users.filter(u => u.role === 'reseller' && u.active).map(u => ({
    ...u, permissions: u.reseller_permissions, client_count: resellers.find(c => c.id === u.client_id)?.client_count ?? 0,
  }));

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>CARGANDO...</div>;

  if (!canClientUsers) {
    return (
      <div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 20, marginBottom: 24 }}>Usuarios</div>
        {canAccount && <CompanyUsersSection showTitle />}
      </div>
    );
  }

  return (
    <div>
      {toast.msg && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 12,
          background: toast.type === 'ok' ? 'rgba(0,229,160,0.1)' : 'rgba(255,77,109,0.1)',
          border: `1px solid ${toast.type === 'ok' ? 'var(--green)' : 'var(--red)'}`,
          color: toast.type === 'ok' ? 'var(--green)' : 'var(--red)',
        }}>
          {toast.type === 'ok' ? '✓' : '✕'} {toast.msg}
        </div>
      )}
      {modal !== null && <UserModal user={modal === 'new' ? null : modal} clients={clients} onClose={() => setModal(null)} onSaved={(created) => {
        load();
        const mail = created?.welcome_email;
        if (mail && !mail.sent) showToast(`Usuario creado, pero el mail con el enlace no salió (${mail.error || 'error de envío'}). Puede pedirlo con «Olvidé mi contraseña».`);
        else showToast(mail ? 'Usuario creado: se le envió un mail para definir su contraseña' : (modal?.id ? 'Usuario actualizado' : 'Usuario creado'), 'ok');
      }} />}
      {confirmDelete && <ConfirmModal title="Desactivar usuario" message={`¿Desactivar el usuario "${confirmDelete.email}"?`} danger onConfirm={() => { handleDelete(confirmDelete.id); setConfirmDelete(null); }} onCancel={() => setConfirmDelete(null)} />}
      {resellerPermsModal && <ResellerPermsModal reseller={resellerPermsModal} onClose={() => setResellerPermsModal(null)} onSaved={() => { load(); showToast('Permisos de reseller guardados', 'ok'); }} />}
      {notifModal && <NotificationPrefsModal user={notifModal} onClose={() => setNotifModal(null)} />}
      {hardDeleteModal && <HardDeleteUserModal user={hardDeleteModal} onClose={() => setHardDeleteModal(null)} onSaved={() => { load(); showToast(`Usuario ${hardDeleteModal.email} eliminado definitivamente`, 'ok'); }} onSwitchToAnonymize={u => { setHardDeleteModal(null); setAnonymizeModal(u); }} />}
      {anonymizeModal && <ConfirmModal title="Anonimizar usuario (GDPR)" message={`¿Anonimizar "${anonymizeModal.email}"? Su email cambiará a deleted-...@anonymized.local y no podrá iniciar sesión.`} danger onConfirm={() => { handleAnonymize(anonymizeModal); setAnonymizeModal(null); }} onCancel={() => setAnonymizeModal(null)} />}

      {resellerView && (
        <div style={{ fontFamily: 'var(--mono)', fontSize: 20, marginBottom: 20 }}>Usuarios</div>
      )}
      {both && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--border)' }} role="tablist">
          {[['company', `Mi empresa${companyCount != null ? ` (${companyCount})` : ''}`], ['clients', `Mis clientes (${users.length})`]].map(([key, label]) => (
            <button key={key} role="tab" aria-selected={tab === key} onClick={() => pickTab(key)}
              style={{ background: 'transparent', border: 'none', borderBottom: `2px solid ${tab === key ? 'var(--accent)' : 'transparent'}`, color: tab === key ? 'var(--text)' : 'var(--muted)', padding: '10px 16px', fontSize: 12, fontFamily: 'var(--mono)', letterSpacing: 1, cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {(!resellerView || tab === 'clients') && (<>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24, gap: 8, flexWrap: 'wrap' }}>
        <div>
          {resellerView ? (
            <>
              {!both && <div style={{ fontFamily: 'var(--mono)', fontSize: 16 }}>Mis clientes ({users.length})</div>}
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{CLIENTS_TAB_TEXT}</div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 20 }}>Usuarios</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{users.length} usuarios registrados</div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {(isAdmin || (isReseller && resellerPerms?.can_manage_users)) && (
            <button onClick={() => setModal('new')} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '8px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700, letterSpacing: 1 }}>{resellerView ? '+ USUARIO DE CLIENTE' : '+ NUEVO USUARIO'}</button>
          )}
        </div>
      </div>

      {resellerView && users.length === 0 ? (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 24, textAlign: 'center', fontFamily: 'var(--mono)', color: 'var(--muted)', fontSize: 12 }}>
          {CLIENTS_EMPTY_TEXT}
        </div>
      ) : null}
      {!(resellerView && users.length === 0) && (isMobile ? (
        <div style={{ marginBottom: isAdmin && resellerUsers.length > 0 ? 32 : 0 }}>
          {sorted.map(u => {
            const client = clients.find(c => c.id === u.client_id);
            return (
              <div key={u.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>EMAIL</div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>{u.email}</div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
                  {!resellerView && (
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>ROL</div>
                      <div><Badge role={u.role} /></div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>ESTADO</div>
                    <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: u.active ? 'var(--green)' : 'var(--muted)' }}>{u.active ? 'ACTIVO' : 'INACTIVO'}</div>
                  </div>
                </div>
                {(client || u.client_name) && (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>{u.role === 'reseller' ? 'EMPRESA' : 'CLIENTE'}</div>
                    <div style={{ fontSize: 13, marginBottom: 10 }}>{client?.name || u.client_name}</div>
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => setNotifModal(u)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>🔔</button>
                  <button onClick={() => setModal(u)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>EDITAR</button>
                  <button onClick={() => setConfirmDelete(u)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>BAJA</button>
                  {isAdmin && <button onClick={() => setAnonymizeModal(u)} style={{ background: 'transparent', border: '1px solid var(--yellow)', color: 'var(--yellow)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>GDPR</button>}
                  {isAdmin && <button onClick={() => setHardDeleteModal(u)} style={{ background: 'transparent', border: '1px solid var(--red)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>ELIM. DEF.</button>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: isAdmin && resellerUsers.length > 0 ? 32 : 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg)' }}>
                {(resellerView ? [['Email', 'email']] : [['Email', 'email'], ['Rol', 'role']]).map(([label, col]) => (
                  <SortTh key={col} label={label} colKey={col} sortKey={sortKey} sortDir={sortDir} onToggle={toggle}
                    style={{ padding: '12px 16px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }} />
                ))}
                {['Cliente'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
                <SortTh label="Estado" colKey="active" sortKey={sortKey} sortDir={sortDir} onToggle={toggle}
                  style={{ padding: '12px 16px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }} />
                <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((u, i) => {
                const client = clients.find(c => c.id === u.client_id);
                return (
                  <tr key={u.id} style={{ borderBottom: i < sorted.length - 1 ? '1px solid var(--border)' : 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '14px 16px', fontWeight: 600 }}>{u.email}</td>
                    {!resellerView && <td style={{ padding: '14px 16px' }}><Badge role={u.role} /></td>}
                    <td style={{ padding: '14px 16px', color: 'var(--muted)', fontSize: 12 }}>{client?.name || u.client_name || '—'}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: u.active ? 'var(--green)' : 'var(--muted)' }}>{u.active ? 'ACTIVO' : 'INACTIVO'}</span>
                    </td>
                    <td style={{ padding: '14px 16px', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button onClick={() => setNotifModal(u)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>🔔</button>
                      <button onClick={() => setModal(u)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>EDITAR</button>
                      <button onClick={() => setConfirmDelete(u)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>BAJA</button>
                      {isAdmin && <button onClick={() => setAnonymizeModal(u)} style={{ background: 'transparent', border: '1px solid var(--yellow)', color: 'var(--yellow)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>GDPR</button>}
                      {isAdmin && <button onClick={() => setHardDeleteModal(u)} style={{ background: 'transparent', border: '1px solid var(--red)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>ELIM. DEF.</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      </>)}

      {isAdmin && !isReseller && (
        <div style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 16 }}>Resellers</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{resellerUsers.length} usuarios reseller activos</div>
            </div>
            <button onClick={() => setModal({ role: 'reseller' })} style={{ background: 'transparent', border: '1px solid var(--yellow)', color: 'var(--yellow)', padding: '7px 14px', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', letterSpacing: 1 }}>+ NUEVO RESELLER</button>
          </div>
          {resellerUsers.length === 0 ? (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 24, textAlign: 'center', fontFamily: 'var(--mono)', color: 'var(--muted)', fontSize: 12 }}>
              No hay usuarios reseller todavía
            </div>
          ) : isMobile ? (
            <div>
              {resellerUsers.map(r => (
                <div key={r.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>EMAIL</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{r.email}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>{r.client_name || '—'}</div>
                  <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--accent)', marginBottom: 10 }}>{r.client_count} clientes</div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                    <button onClick={() => setResellerPermsModal(r)} style={{ background: 'transparent', border: '1px solid var(--yellow)', color: 'var(--yellow)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>PERMISOS</button>
                    <button onClick={() => navigate('/clients')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>VER CLIENTES</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--bg)' }}>
                    {['Email', 'Empresa', 'Clientes', 'Permisos activos', ''].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resellerUsers.map((r, i) => (
                    <tr key={r.id} style={{ borderBottom: i < resellerUsers.length - 1 ? '1px solid var(--border)' : 'none' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,209,102,0.03)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '14px 16px', fontWeight: 600 }}>{r.email}</td>
                      <td style={{ padding: '14px 16px', fontSize: 12, color: 'var(--muted)' }}>{r.client_name || '—'}</td>
                      <td style={{ padding: '14px 16px', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)' }}>{r.client_count}</td>
                      <td style={{ padding: '14px 16px', fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                        {r.permissions ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {r.permissions.can_view_clients    && <span style={{ padding: '2px 6px', background: 'rgba(255,209,102,0.1)', color: 'var(--yellow)', borderRadius: 2, fontSize: 10 }}>ver</span>}
                            {r.permissions.can_create_clients  && <span style={{ padding: '2px 6px', background: 'rgba(255,209,102,0.1)', color: 'var(--yellow)', borderRadius: 2, fontSize: 10 }}>crear</span>}
                            {r.permissions.can_edit_clients    && <span style={{ padding: '2px 6px', background: 'rgba(255,209,102,0.1)', color: 'var(--yellow)', borderRadius: 2, fontSize: 10 }}>editar</span>}
                            {r.permissions.can_manage_jobs     && <span style={{ padding: '2px 6px', background: 'rgba(255,209,102,0.1)', color: 'var(--yellow)', borderRadius: 2, fontSize: 10 }}>jobs</span>}
                            {r.permissions.can_manage_servers  && <span style={{ padding: '2px 6px', background: 'rgba(255,209,102,0.1)', color: 'var(--yellow)', borderRadius: 2, fontSize: 10 }}>servers</span>}
                            {r.permissions.can_view_reports    && <span style={{ padding: '2px 6px', background: 'rgba(255,209,102,0.1)', color: 'var(--yellow)', borderRadius: 2, fontSize: 10 }}>logs</span>}
                            {r.permissions.can_manage_billing  && <span style={{ padding: '2px 6px', background: 'rgba(255,209,102,0.1)', color: 'var(--yellow)', borderRadius: 2, fontSize: 10 }}>billing</span>}
                          </div>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '14px 16px', display: 'flex', gap: 4 }}>
                        <button onClick={() => setResellerPermsModal(r)} style={{ background: 'transparent', border: '1px solid var(--yellow)', color: 'var(--yellow)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>PERMISOS</button>
                        <button onClick={() => navigate('/clients')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>VER CLIENTES</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {showCompany && (
        <div style={{ display: tab === 'company' ? 'block' : 'none' }}>
          <CompanyUsersSection showTitle={!both} onLoaded={(list) => setCompanyCount(list.filter(u => u.active).length)} />
        </div>
      )}
    </div>
  );
}
